import { ApolloCache } from '@apollo/client';
import { blinkConstants } from '@thoughtspot/blink-constants';
import { useEmbedContext } from '@thoughtspot/blink-context';
import React from 'react';
import { useEffectOnce, useUpdateEffect } from 'react-use';
import { Subject } from 'rxjs';
import { getAnswerPageConfig } from '/@contexts/answer-context';
import { useAppContext } from '/@contexts/global-app-context';
import {
    getAnswerIdForAnswerEditSession,
    useGetSelectedSourcesQuery,
} from '/@services/answer/answer-service';
import { stripTypeNameFromToken } from '/@services/worksheet/worksheet-service';
import { getVisualOverridesFromEmbedConfig } from '/@utils/embed.util';
import {
    convertTokensToInputTokens,
    getTextForTokens,
    isQueryEmpty,
} from '/@utils/sage.utils';
import { getTotalTokens, hasUnresolvedTokens } from '../../apollo-client.util';
import {
    useApolloClient,
    useMutation,
} from '../../utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import { useBachQuery } from '../bach/bach';
import {
    AnsQueryBuilderSearchDocument,
    AnsQueryBuilderSearchMutation,
    AnsQueryBuilderSearchMutationVariables,
    DataCancelDocument,
    DataCancelMutation,
    DataCancelMutationVariables,
    DoSearchDocument,
    DoSearchMutation,
    DoSearchMutationVariables,
    FalconDataType,
    GetAnswerPhrasesDocument,
    GetAnswerPhrasesQuery,
    GetAnswerPhrasesQueryVariables,
    GetSageSearchResponseDocument,
    GetSageSearchResponseQuery,
    GetSageSearchResponseQueryVariables,
    LoadingState,
    PhraseType,
    RecognizedTokenType,
    ResolveSageTokenAmbiguityDocument,
    ResolveSageTokenAmbiguityMutation,
    ResolveSageTokenAmbiguityMutationVariables,
    SageFeatureFlag,
    SagePhrase,
    SageRequestFlag,
    SageSearchResponse as RawSageSearchResponse,
    SageTokenInput,
    SetSageSearchButtonTourSeenDocument,
    SetSageSearchButtonTourSeenMutation,
    SetSageSearchButtonTourSeenMutationVariables,
} from '../generated/graphql-types';
import { useUpdateAnswerMutation } from './answer-update-service';
import { useSearchMutationBase, useSearchQueryBase } from './search-base-hooks';
import { isQueryCancellationEnabled } from './search-cancel.util';
import {
    clearAnswerVizInCache,
    clearStaleAnswerPropertiesInCache,
    getPhrasesOrDefaultEmptyPhrase,
} from './search-service.util';
import { buildVisualOverridesPayload } from './visual-overrides.util';

export interface SageSearchResponse extends RawSageSearchResponse {
    isFromAnswerUpdate: boolean;
}

/*
 * Emit on this subject to cause any in-progress sage request to get aborted. If no sage request is
 * in progress, it is a no-op (it will NOT remember to abort the next request or anything).
 */
export const abortInProgressSageRequestSubject = new Subject<void>();

/*
 * Maintains and returns the latest sage search result data (phrases, completions, etc). Will
 * monitor for changes to this data from new sage requests, as well as new phrases from the answer
 * which come from bach requests (eg add column from left panel), and return the latest from both
 * sources aggregated together.
 *
 * NOTE: This hook is designed to be used continuously starting from the creation of the answer
 * session. If it's called on an existing answer session that had gone through changes from before
 * the hook is first called, then it may not behave as expected. You should consider using
 * sageQueryState from the answer context instead for such scenarios.
 *
 * TODO(Rifdhan) find a way to use this inside useSageSearchMutation to avoid code duplication
 */
export function useSageSearchQuery(
    answerSessionId: string,
    initialSageQueryState?: RawSageSearchResponse,
): SageSearchResponse | null {
    const searchResponseQueryData = useSearchQueryBase<
        GetSageSearchResponseQuery,
        GetSageSearchResponseQueryVariables
    >(answerSessionId, GetSageSearchResponseDocument, {
        fetchPolicy: 'cache-only',
    });
    const answerPhrasesQueryData = useBachQuery<
        GetAnswerPhrasesQuery,
        GetAnswerPhrasesQueryVariables
    >(answerSessionId, GetAnswerPhrasesDocument);

    const [
        latestSageSearchResponse,
        setLatestSageSearchResponse,
    ] = React.useState<SageSearchResponse | null>(
        initialSageQueryState
            ? {
                  ...initialSageQueryState,
                  isFromAnswerUpdate: true,
              }
            : null,
    );
    let didUpdateLatestSageQueryDataUsingSageResponse = false;

    // Whenever we get new data from a regular sage request, update our local copy of the latest
    // data using that new data
    React.useEffect(() => {
        if (!searchResponseQueryData.data?.getSageSearchResponse) {
            return;
        }

        setLatestSageSearchResponse({
            ...searchResponseQueryData.data.getSageSearchResponse,
            phrases: getPhrasesOrDefaultEmptyPhrase(
                searchResponseQueryData.data.getSageSearchResponse?.phrases,
            ),
            isFromAnswerUpdate: false,
        });
        didUpdateLatestSageQueryDataUsingSageResponse = true;
    }, [searchResponseQueryData.data?.getSageSearchResponse?.phrases]);

    // Whenever the answer is updated, check whether there is updated token data within it. If so,
    // update our local copy of the latest data using that new data. If there is no new token data
    // in the answer data, then we assume the answer was updated in a way that does not impact the
    // sage tokens.
    React.useEffect(() => {
        // Note that the answer may be empty (eg removed the last column via left panel), in which
        // case we still want to trigger this update. A phrases array that is defined but empty is
        // a valid case to apply an update here.
        if (!answerPhrasesQueryData.data?.getAnswer.answer.phrases) {
            return;
        }

        // If we got new phrase data in both the sage response and the answer object during the
        // same render cycle, then check the timestamps and let the chronologicically newer
        // response take precedence. This is especially relevant when creating a new Search Service
        // instance on a pre-existing answer session, to ensure correct initialization.
        if (didUpdateLatestSageQueryDataUsingSageResponse) {
            const answerPhrasesTimestamp = parseInt(
                answerPhrasesQueryData.data?.getAnswer.answer?.phrasesTimestamp,
                10,
            );
            const sagePhrasesTimestamp = parseInt(
                searchResponseQueryData.data?.getSageSearchResponse
                    ?.phrasesTimestamp,
                10,
            );
            if (answerPhrasesTimestamp < sagePhrasesTimestamp) return;
        }

        setLatestSageSearchResponse({
            id: null,
            hashKey: answerPhrasesQueryData.data?.getAnswer.answer.hashKey,
            phrases: getPhrasesOrDefaultEmptyPhrase(
                answerPhrasesQueryData.data?.getAnswer.answer.phrases,
            ),
            phrasesTimestamp:
                answerPhrasesQueryData.data?.getAnswer.answer.phrasesTimestamp,
            completions: latestSageSearchResponse?.completions,
            isQueryEmpty:
                answerPhrasesQueryData.data?.getAnswer.answer.phrases.length ===
                0,
            hasUnresolvedTokens: hasUnresolvedTokens(
                answerPhrasesQueryData.data?.getAnswer.answer.phrases,
            ),
            totalTokens: getTotalTokens(
                answerPhrasesQueryData.data?.getAnswer.answer.phrases,
            ),
            isFromAnswerUpdate: true,
        });
    }, [answerPhrasesQueryData.data?.getAnswer.answer.phrases]);

    // If the caller passed an initial state, we apply it once during the first render cycle. This
    // effect is positioned after the two above effects which watch the sage search response and
    // answer response, so that this effect takes final priority, exceeding that of the two above.
    useEffectOnce(() => {
        if (initialSageQueryState)
            setLatestSageSearchResponse({
                ...initialSageQueryState,
                isFromAnswerUpdate: true,
            });
    });

    return latestSageSearchResponse;
}

export function useSageSearchMutation(
    answerSessionId: string,
    autoUpdateAnswerAfterSearch = false,
    includeTableDataWithAnswer?: boolean,
    initialSageQueryState?: RawSageSearchResponse,
    onAnswerUpdateStart?: () => void,
) {
    const client = useApolloClient();
    const { sessionService, featureFlags } = useAppContext();
    const isCancellationEnabled = isQueryCancellationEnabled(
        sessionService,
        featureFlags,
    );

    const { data: selectedSourceGuids } = useGetSelectedSourcesQuery(
        answerSessionId,
    );

    const { data: searchResponseQueryData } = useSearchQueryBase<
        GetSageSearchResponseQuery,
        GetSageSearchResponseQueryVariables
    >(answerSessionId, GetSageSearchResponseDocument, {
        fetchPolicy: 'cache-only',
    });
    const { data: answerPhrasesQueryData } = useBachQuery<
        GetAnswerPhrasesQuery,
        GetAnswerPhrasesQueryVariables
    >(answerSessionId, GetAnswerPhrasesDocument);

    // We want to avoid making too many requests to the backend while the user is editing the query
    // very quickly, so we use a debounce whenever appropriate
    const [
        sageSearchImmediate,
        sageSearchThrottled,
        cancelSageSearch,
        { loading: sageSearchLoading, error: sageSearchError },
    ] = useSearchMutationBase<DoSearchMutation, DoSearchMutationVariables>(
        answerSessionId,
        DoSearchDocument,
    );

    /**
     * This error-remapping is a HACK to show still indexing banner alert
     * when `DoSearch` mutation fails due to the user metadata still being indexed.
     */
    React.useEffect(() => {
        const errorObj = !(sageSearchError?.networkError as any)?.result
            ?.errors?.[0]
            ? (sageSearchError?.graphQLErrors?.[0] as any)
            : (sageSearchError?.networkError as any)?.result?.errors?.[0];
        if (errorObj && errorObj?.extensions?.code === '33') {
            errorObj.extensions.code =
                blinkConstants.blinkGeneratedErrors.SAGE_INDEXING;
        }
    }, [sageSearchError]);

    const embedConfig = useEmbedContext();
    const { isEmbedded } = embedConfig;
    const visualOverrides = isEmbedded
        ? getVisualOverridesFromEmbedConfig(embedConfig)
        : null;
    const transformedVisualOverrides = visualOverrides
        ? buildVisualOverridesPayload(visualOverrides)
        : null;

    const {
        updateAnswer,
        updateAnswerWithDebounce,
        cancelUpdateAnswer,
    } = useUpdateAnswerMutation(
        answerSessionId,
        includeTableDataWithAnswer,
        onAnswerUpdateStart,
    );

    // Due to an Apollo bug, if the current request gets aborted, its loading flag will remain
    // stuck as true. To work around this issue, we use this additional flag to ignore the
    // incorrect lodaing flag state in the case of aborting the latest sage request. We reset it
    // when the next sage request begins. This is fixed in Apollo 3.8+, can remove when we upgrade.
    // See: https://github.com/apollographql/apollo-client/issues/9583
    const [
        didAbortLastSageRequest,
        setDidAbortLastSageRequest,
    ] = React.useState(false);
    React.useEffect(() => {
        setDidAbortLastSageRequest(false);
    }, [sageSearchLoading]);

    // If anyone emits an event to cancel any in-progress sage request, listen for and handle that
    React.useEffect(() => {
        const subscription = abortInProgressSageRequestSubject.subscribe(() => {
            cancelSageSearch();
            setDidAbortLastSageRequest(true);
        });
        return () => subscription.unsubscribe();
    }, []);

    // Wrap the loading flag in a ref so that older instances of this hook can still get the latest
    // information on whether a sage request is pending or not
    const sageSearchLoadingRef = React.useRef(sageSearchLoading);
    sageSearchLoadingRef.current = sageSearchLoading;

    const currentDisplayedAnswerHashKeyRef = React.useRef<string>(null);

    const [
        latestSageQueryData,
        setLatestSageQueryData,
    ] = React.useState<SageSearchResponse | null>(
        initialSageQueryState
            ? {
                  ...initialSageQueryState,
                  isFromAnswerUpdate: true,
              }
            : null,
    );
    let didUpdateLatestSageQueryDataUsingSageResponse = false;

    // Freshest sage hashKey, readable synchronously (e.g. from the response effect
    // below, before the re-render) for the cancellation baseline in the container.
    const latestSageHashKeyRef = React.useRef<string | null>(
        initialSageQueryState?.hashKey ?? null,
    );
    if (isCancellationEnabled) {
        latestSageHashKeyRef.current = latestSageQueryData?.hashKey ?? null;
    }
    const getLatestSageHashKey = React.useCallback(
        () => latestSageHashKeyRef.current,
        [],
    );

    const shouldUpdateAnswerAfterSearchResponseReceivedRef = React.useRef(
        false,
    );
    // When a deferred update was a rerun, remember it so the force-rerun intent
    // survives the deferral (a rerun re-executes the current query regardless of
    // whether the hash changed; otherwise hash-gating would silently drop it).
    const shouldRerunAfterSearchResponseRef = React.useRef(false);

    const updateAnswerIfHashKeyChanged = React.useCallback(
        (newSageHashKey: string, isNewQueryEmpty: boolean) => {
            // If the currently displayed answer has the same hashKey as the new sage hashKey, we don't
            // need to trigger an answer update. This applies even if both hashKeys are empty (which is
            // the case when the query is empty and the answer is blank).
            if (newSageHashKey === currentDisplayedAnswerHashKeyRef.current) {
                return false;
            }

            currentDisplayedAnswerHashKeyRef.current = newSageHashKey;

            updateAnswer(!isNewQueryEmpty, transformedVisualOverrides);
            return true;
        },
        [updateAnswer],
    );

    // Whenever we get a new sage response, update our local token state. Also, check if we need to
    // trigger an answer update with the new query.
    React.useEffect(() => {
        if (!searchResponseQueryData?.getSageSearchResponse) {
            return;
        }

        // Set synchronously so a deferred update fired below (onAnswerUpdateStart)
        // sees the settled hashKey, not the pre-parse value still in React state.
        if (isCancellationEnabled) {
            latestSageHashKeyRef.current =
                searchResponseQueryData.getSageSearchResponse.hashKey;
        }

        setLatestSageQueryData({
            ...searchResponseQueryData.getSageSearchResponse,
            phrases: getPhrasesOrDefaultEmptyPhrase(
                searchResponseQueryData.getSageSearchResponse?.phrases,
            ),
            isFromAnswerUpdate: false,
        });
        didUpdateLatestSageQueryDataUsingSageResponse = true;

        if (shouldUpdateAnswerAfterSearchResponseReceivedRef.current) {
            shouldUpdateAnswerAfterSearchResponseReceivedRef.current = false;
            const shouldForceRerun =
                isCancellationEnabled &&
                shouldRerunAfterSearchResponseRef.current;
            shouldRerunAfterSearchResponseRef.current = false;
            if (shouldForceRerun) {
                // A deferred rerun must force the data query even when the hash is
                // unchanged (e.g. re-running a cancelled query while the bar is
                // focused, which keeps a sage completions request in flight).
                // Gated behind cancellation so non-cancellation reruns keep the
                // original hash-gated behaviour.
                updateAnswer(true, transformedVisualOverrides);
            } else {
                updateAnswerIfHashKeyChanged(
                    // Must use the new hash key directly since sageQueryData.hashkey is still the old
                    // value during the current render (we just called its setter above)
                    searchResponseQueryData.getSageSearchResponse.hashKey,
                    isQueryEmpty(
                        searchResponseQueryData?.getSageSearchResponse?.phrases,
                    ),
                );
            }
        }
    }, [searchResponseQueryData?.getSageSearchResponse.phrases]);

    // Below is a short-term workaround to an issue with the React render lifecyle, where some
    // aborted renders cause Effects to not get run when they should have. In this logic, we track
    // the last set of answer phrases processed in the main Effect (around line 365). If we see a
    // set of answer phrases but React did not run any Effects during that render cycle, then we
    // manually set a dummy dependency variable to trigger that Effect in the next render cycle.
    // This fix is meant to be temporary, and a long-term fix will be provided in SCAL-272434.
    const lastProcessedAnswerPhrasesRef = React.useRef(null);
    const dummyTriggerDepRef = React.useRef(0);
    const dummyTriggerDepUpdateCbRef = React.useRef(null);
    if (dummyTriggerDepUpdateCbRef.current) {
        // If we have an update callback, run it now and clear it
        dummyTriggerDepUpdateCbRef.current();
        dummyTriggerDepUpdateCbRef.current = null;
    } else if (
        lastProcessedAnswerPhrasesRef.current !==
        answerPhrasesQueryData?.getAnswer.answer.phrases
    ) {
        // If the answer phrases have changed since the last processed set, queue an callback to
        // trigger a dummy update to force the Effect to run
        dummyTriggerDepUpdateCbRef.current = () => {
            dummyTriggerDepRef.current = Math.random();
        };
    }
    React.useEffect(() => {
        // If React ran Effects in this render cycle, this Effect will also run and clear the
        // queued callback because we don't need to artificially cause the Effect to trigger. If
        // React did not run any Effects, this won't happen and the callback will run next render.
        dummyTriggerDepUpdateCbRef.current = null;
    });

    // Whenever the answer is updated, check whether there is updated token data within it. If so,
    // apply the new tokens to the sage bar. If there is no new token data in the answer data, then
    // we assume the answer was updated in a way that does not impact the sage tokens.
    React.useEffect(() => {
        // Record the last processed phrases
        lastProcessedAnswerPhrasesRef.current =
            answerPhrasesQueryData?.getAnswer.answer.phrases;

        // Note that the answer may be empty (eg removed the last column via left panel), in which
        // case we still want to trigger this update. A phrases array that is defined but empty is
        // a valid case to apply an update here.
        if (!answerPhrasesQueryData?.getAnswer.answer.phrases) {
            return;
        }

        // If we got new phrase data in both the sage response and the answer object during the
        // same render cycle, then check the timestamps and let the chronologicically newer
        // response take precedence. This is especially relevant when creating a new Search Service
        // instance on a pre-existing answer session, to ensure correct initialization.
        if (didUpdateLatestSageQueryDataUsingSageResponse) {
            const answerPhrasesTimestamp = parseInt(
                answerPhrasesQueryData?.getAnswer.answer.phrasesTimestamp,
                10,
            );
            const sagePhrasesTimestamp = parseInt(
                searchResponseQueryData?.getSageSearchResponse.phrasesTimestamp,
                10,
            );
            if (answerPhrasesTimestamp < sagePhrasesTimestamp) return;
        }

        if (!answerPhrasesQueryData?.getAnswer.answer.updateOnlyPhrases) {
            currentDisplayedAnswerHashKeyRef.current =
                answerPhrasesQueryData.getAnswer.answer.hashKey;
        }
        setLatestSageQueryData({
            id: null,
            hashKey: answerPhrasesQueryData.getAnswer.answer.hashKey,
            phrases: getPhrasesOrDefaultEmptyPhrase(
                answerPhrasesQueryData.getAnswer.answer.phrases,
            ),
            phrasesTimestamp:
                answerPhrasesQueryData.getAnswer.answer.phrasesTimestamp,
            completions: latestSageQueryData?.completions,
            isQueryEmpty:
                answerPhrasesQueryData.getAnswer.answer.phrases.length === 0,
            hasUnresolvedTokens: hasUnresolvedTokens(
                answerPhrasesQueryData.getAnswer.answer.phrases,
            ),
            totalTokens: getTotalTokens(
                answerPhrasesQueryData.getAnswer.answer.phrases,
            ),
            isFromAnswerUpdate: true,
        });
    }, [
        answerPhrasesQueryData?.getAnswer.answer.phrases,
        dummyTriggerDepRef.current,
    ]);

    // If the caller passed an initial state, we apply it once during the first render cycle. This
    // effect is positioned after the two above effects which watch the sage search response and
    // answer response, so that this effect takes final priority, exceeding that of the two above.
    useEffectOnce(() => {
        if (initialSageQueryState)
            setLatestSageQueryData({
                ...initialSageQueryState,
                isFromAnswerUpdate: true,
            });
    });

    useUpdateEffect(() => {
        if (!searchResponseQueryData) {
            return;
        }

        // If the hashKey hasn't changed, no need to do an update
        if (
            searchResponseQueryData?.getSageSearchResponse.hashKey ===
            currentDisplayedAnswerHashKeyRef.current
        ) {
            return;
        }

        // We debounce answer updates that occur in the background while the user is editing the
        // sage query. This reduces the perceived lagginess while typing, since it allows the
        // animated motion of the sage completions dropdown to finish before we start an answer
        // update (which is very heavy and will slow down the rendering performance of the page).
        updateAnswerWithDebounce(
            isQueryEmpty(
                searchResponseQueryData?.getSageSearchResponse?.phrases,
            ),
            transformedVisualOverrides,
        );
    }, [
        autoUpdateAnswerAfterSearch
            ? searchResponseQueryData?.getSageSearchResponse.hashKey
            : null,
    ]);

    const sageSearch = React.useCallback(
        (
            flatTokens: SageTokenInput[],
            caretTokenIdx: number,
            charOffsetInCaretToken: number,
            nDesiredCompletions: number,
            throttleable = false,
            sageRequestFlags?: SageRequestFlag[],
            sageFeatureFlags?: SageFeatureFlag[],
        ) => {
            // If this request will result in an empty answer, then immediately clear the answer viz
            // from cache so that the UI updates without having to wait for the requests to complete
            if (getTextForTokens(flatTokens).trim().length === 0) {
                // Put this in a microTask becuase it causes the viz to update, which slows down the UI
                queueMicrotask(() => {
                    clearStaleAnswerPropertiesInCache(client, answerSessionId);
                    clearAnswerVizInCache(client, answerSessionId);
                });
            }

            // TODO(Rifdhan) SCAL-57991 move to prism
            const tokens: SageTokenInput[] = flatTokens.map(
                (token: SageTokenInput) => ({
                    text: token.text,
                    hasSpaceAfter: token.hasSpaceAfter,
                    recognizedTokenJson:
                        token.recognizedTokenJson ?? ('' as any),
                }),
            );

            const variables: Omit<DoSearchMutationVariables, 'session'> = {
                selectedSourceGuids,
                tokens,
                editedTokenIdx: caretTokenIdx,
                charOffsetInEditedToken: charOffsetInCaretToken,
                nDesiredCompletions,
            };
            if (sageRequestFlags) {
                variables.sageRequestFlags = sageRequestFlags;
            }
            if (sageFeatureFlags) {
                variables.sageFeatureFlags = sageFeatureFlags;
            }

            const sageSearchFn = throttleable
                ? sageSearchThrottled
                : sageSearchImmediate;
            setDidAbortLastSageRequest(false);
            sageSearchFn({
                variables,
            });
        },
        [
            client,
            answerSessionId,
            selectedSourceGuids,
            sageSearchThrottled,
            sageSearchImmediate,
            setDidAbortLastSageRequest,
        ],
    );

    const updateAnswerAfterSearch = React.useCallback(
        (config: { rerun?: boolean } = {}) => {
            // We want to trigger an answer update after any pending search requests complete. If there
            // are no pending search requests, then we update the answer immediately.
            // See definition of didAbortLastSageRequest for explanation of why it's needed
            if (sageSearchLoadingRef.current && !didAbortLastSageRequest) {
                shouldUpdateAnswerAfterSearchResponseReceivedRef.current = true;
                shouldRerunAfterSearchResponseRef.current = !!config.rerun;
                return true;
            }
            if (config.rerun) {
                updateAnswer(true, transformedVisualOverrides);
                return true;
            }
            return updateAnswerIfHashKeyChanged(
                latestSageQueryData?.hashKey,
                isQueryEmpty(latestSageQueryData?.phrases),
            );
        },
        [
            updateAnswerIfHashKeyChanged,
            latestSageQueryData?.hashKey,
            latestSageQueryData?.phrases,
            didAbortLastSageRequest,
            updateAnswer,
        ],
    );

    const cancelSearchRunPhase = React.useCallback(() => {
        shouldUpdateAnswerAfterSearchResponseReceivedRef.current = false;
        shouldRerunAfterSearchResponseRef.current = false;
        updateAnswerWithDebounce.cancel();
        cancelUpdateAnswer();
        currentDisplayedAnswerHashKeyRef.current =
            answerPhrasesQueryData?.getAnswer.answer.hashKey ?? null;
        cancelSageSearch();
        setDidAbortLastSageRequest(true);
    }, [
        answerPhrasesQueryData?.getAnswer.answer.hashKey,
        cancelSageSearch,
        cancelUpdateAnswer,
        updateAnswerWithDebounce,
    ]);

    const setQueryText = React.useCallback(
        (queryText: string, nDesiredCompletions: number) => {
            // TODO(Rifdhan/Ayon): we want to have a sage API where we can provide the query and sage
            // will parse and apply it. Until then, we perform this workaround of creating a single
            // phrase with the text (an optimistic response) while making a sage request to get sage to
            // attempt to tokenize the text.
            setLatestSageQueryData({
                ...latestSageQueryData,
                phrases: [
                    {
                        type: PhraseType.FilterPhrase,
                        tokens: [
                            {
                                type: RecognizedTokenType.Unrecognized,
                                dataType: FalconDataType.Unknown,
                                text: queryText,
                                hasSpaceAfter: true,
                                isFromFormula: false,
                                isFromCohort: false,
                                recognizedTokenJson: '' as any,
                            },
                        ],
                        isComplete: true,
                        isAmbiguous: false,
                    },
                ],
                totalTokens: 1,
                isFromAnswerUpdate: true,
            });
            sageSearch(
                [
                    {
                        text: queryText,
                        hasSpaceAfter: true,
                        recognizedTokenJson: '' as any,
                    },
                ],
                0,
                0,
                nDesiredCompletions,
                false,
                [SageRequestFlag.DoBestEffortTokenization],
            );
        },
        [latestSageQueryData, sageSearch],
    );

    return {
        sageSearch,
        updateAnswerAfterSearch,
        setQueryText,
        cancelSearchRunPhase,
        getLatestSageHashKey,
        data: latestSageQueryData,
        error: sageSearchError,
        // Whether a sage search request is loading (this is the primary loading flag)
        // See definition of didAbortLastSageRequest for explanation of why it's needed
        sageSearchLoading: sageSearchLoading && !didAbortLastSageRequest,
        // Whether an external request is loading, which will update phrases state (eg a bach
        // request to add a column to the query)
        externalUpdateLoading:
            searchResponseQueryData?.getSageSearchResponse?.phrasesLoading ===
            LoadingState.InProgress,
    };
}

export function useResolveSageTokenAmbiguityMutation(answerSessionId: string) {
    const { data: selectedSourceGuids } = useGetSelectedSourcesQuery(
        answerSessionId,
    );
    const { data: sageQueryData } = useSearchQueryBase<
        GetSageSearchResponseQuery,
        GetSageSearchResponseQueryVariables
    >(answerSessionId, GetSageSearchResponseDocument, {
        fetchPolicy: 'cache-only',
    });

    const [
        resolveSageTokenAmbiguity,
        ,
        ,
        { loading, error },
    ] = useSearchMutationBase<
        ResolveSageTokenAmbiguityMutation,
        ResolveSageTokenAmbiguityMutationVariables
    >(answerSessionId, ResolveSageTokenAmbiguityDocument);

    const resolveSageTokenAmbiguityWrapper = React.useCallback(
        (resolveTokenIdx: number) => {
            resolveSageTokenAmbiguity({
                variables: {
                    selectedSourceGuids,
                    tokens: convertTokensToInputTokens(
                        sageQueryData?.getSageSearchResponse?.phrases
                            .map(phrase => phrase.tokens)
                            .flat(),
                    ),
                    resolveTokenIdx,
                },
            });
        },
        [
            resolveSageTokenAmbiguity,
            selectedSourceGuids,
            sageQueryData?.getSageSearchResponse?.phrases,
        ],
    );

    return {
        resolveSageTokenAmbiguity: resolveSageTokenAmbiguityWrapper,
        loading,
        error,
    };
}

export function useAnswerQueryBuilderSearch(sessionId: string) {
    const [doQBR, , , { data, loading, error }] = useSearchMutationBase<
        AnsQueryBuilderSearchMutation,
        Omit<AnsQueryBuilderSearchMutationVariables, 'session'>
    >(sessionId, AnsQueryBuilderSearchDocument);

    return {
        loading,
        error,
        search: doQBR,
        tokens: data?.Answer__queryBuilderSearch.tokens.map(t => ({
            ...stripTypeNameFromToken(t),
        })) as any[],
    };
}

export const updatePhrasesInCache = (
    cache: ApolloCache<object>,
    answerSessionId: string,
    phrases: SagePhrase[],
) => {
    const id = cache.identify({
        id: getAnswerIdForAnswerEditSession(cache, answerSessionId),
        __typename: 'Answer',
    });

    try {
        cache.modify({
            id,
            fields: {
                phrases() {
                    return phrases;
                },
            },
        });
    } catch (error) {
        if (!error?.message?.includes('already recomputing')) {
            throw error;
        }
    }
};

export function useGetReferencedSourcesInSageQuery() {
    const { sageQueryState } = getAnswerPageConfig();

    return React.useMemo(() => {
        const referencedSourcesInSageQuery = new Map<string, string>();

        (sageQueryState?.phrases || []).forEach(phrase => {
            phrase.tokens.forEach(token => {
                // `source` is not available for empty tokens (empty search bar).
                if (token.source)
                    referencedSourcesInSageQuery.set(
                        token.source.id,
                        token.source.name,
                    );
            });
        });

        return referencedSourcesInSageQuery;
    }, [sageQueryState?.phrases]);
}

export const useDataCancelMutation = () => {
    const [dataCancelMutation, { data, error, loading }] = useMutation<
        DataCancelMutation,
        DataCancelMutationVariables
    >(DataCancelDocument);

    const dataCancel = React.useCallback(
        async (cancelIds: string[]): Promise<boolean> => {
            if (!cancelIds?.length) {
                return false;
            }

            const response = await dataCancelMutation({
                variables: {
                    cancelIds,
                },
            });

            return response?.data?.data_cancel ?? false;
        },
        [dataCancelMutation],
    );

    return {
        dataCancel,
        data: data?.data_cancel ?? false,
        error,
        loading,
    };
};

export const useSetSageSearchButtonTourSeen = () => {
    const [
        _setSageSearchButtonTourSeen,
        { data, error, loading },
    ] = useMutation<
        SetSageSearchButtonTourSeenMutation,
        SetSageSearchButtonTourSeenMutationVariables
    >(SetSageSearchButtonTourSeenDocument);

    return {
        data: data?.Session__setSageSearchButtonTourSeen,
        error,
        loading,
        setSageSearchButtonTourSeen: (sageSearchButtonTourSeen: boolean) =>
            _setSageSearchButtonTourSeen({
                variables: {
                    sageSearchButtonTourSeen,
                },
            }),
    };
};
